import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  InfiniteScrollBox,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "../index.scss";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import utilsCommon from "../../../shared/utils/common";
import { t, use } from "i18next";
import {
  FULL_FILTER_LOG_BY_TYPE,
  INIT_LOG_FILTER,
} from "../../Workgroup/WorkgroupLog";
import DateDropdown from "../../../components/DateDropdown";
import useLogStore from "../../../redux/dispatcher/useLogStore";
import { LogItem } from "../../../types/files";
import { historyIconByName } from "../../Workgroup/WorkgroupLog/CompressWorkgroupLog";
import RevisionTag from "../../../components/RevisionTag";
import { handleDocDownload, handleDocPreview } from "../../../shared/utils/api";
import useDocumentStore from "../../../redux/dispatcher/useDocumentStore";
import EmptyFilterPage from "../../../components/EmptyPage/EmptyFilterPage";
import utilsClient from "../../../shared/utils/client";
import { ChatLogItem } from "../../../types/chat";
import i18n from "fds/src/locales/i18n";
import classNames from "classnames";

dayjs.extend(utc);

const ClientHistory = ({
  workgroupId,
  refreshCnt,
}: {
  workgroupId: string;
  refreshCnt: number;
}) => {
  const historyListNewCard = useRef([] as any[]);

  const { getContentDownload } = useDocumentStore();
  const { logState, getNewLogList } = useLogStore();
  const toast = useToast();
  const [buttonClick, setButtonClick] = useState(0);
  const [dateFilter, changeDateFilter] = useState<string>("all");
  const [filter, changeFilter] = useState(INIT_LOG_FILTER);
  const [key, setKey] = useState(0); // state용

  useEffect(() => {
    const {
      filterChat,
      filterDocument,
      filterWebMeeting,
      filterWorkgroup,
      ...newFilter
    } = filter;
    getNewLogList(
      filter.logId
        ? {
            ...newFilter,
            ...FULL_FILTER_LOG_BY_TYPE,
            rowRange: 30,
            workgroupId: workgroupId,
          }
        : {
            ...filter,
            ...FULL_FILTER_LOG_BY_TYPE,
            rowRange: 30,
            workgroupId: workgroupId,
          }
    );
  }, [filter, refreshCnt]);
  useEffect(() => {
    let newDataCardContent = [] as any[];
    logState.newLogList?.logResults?.forEach((item: LogItem) =>
      newDataCardContent.push({
        id: item.logId,
        data: renderHistoryLogItem(item),
      })
    );
    historyListNewCard.current = newDataCardContent;
    setKey(key + 1);
  }, [logState.newLogList]);
  const changeFilterByKey = (key: string, value: any) => {
    if (key === "date") {
      if (typeof value !== "object") {
        changeDateFilter(value);
        changeFilter({
          ...filter,
          logId: undefined,
          startDate: utilsCommon.getStartPeriod(value),
          endDate: dayjs().valueOf(),
        });
      } else {
        changeDateFilter("custom");
        changeFilter({
          ...filter,
          logId: undefined,
          startDate: dayjs(value?.start ?? undefined).valueOf(),
          endDate: dayjs(value?.end ?? undefined).valueOf(),
        });
      }
      document.getElementById("docTable")?.scrollTo(0, 0);
      return;
    }
  };
  const renderProperDetailLog = (row: LogItem) => {
    const chat = row?.chatLogData
      ? (JSON.parse(row?.chatLogData)[0] as ChatLogItem)
      : undefined;
    return row.historyType === 0 ? (
      <Box classes="mg ml-12" maxWidth={"calc(100% - 80px)"}>
        <Tooltip message={row.desc}>
          <Typography type="body2_rg" ellipsis>
            {row.desc}
          </Typography>
        </Tooltip>
      </Box>
    ) : row.historyType === 1 ? (
      <Box
        classes="mg ml-12"
        maxWidth={
          i18n.language.includes("ko")
            ? "calc(100% - 150px)"
            : "calc(100% - 200px)"
        }
        alignItems="center"
      >
        <Tooltip message={row.documnetName}>
          <Typography
            classes={classNames("mg", {
              "hover-text-underline": row.logTypeDbValue.includes("document"),
            })}
            type="body2_rg"
            ellipsis
            onClick={(e: any) => {
              e.stopPropagation();
              row.syncId &&
                handleDocPreview({
                  permission: "view",
                  workgroupId: workgroupId,
                  syncId: row.syncId,
                  toast,
                  t,
                  revisionNo: row?.revisionNo,
                });
            }}
          >
            {row.documnetName}
          </Typography>
        </Tooltip>
        <RevisionTag revisionNo={row.revisionNo} classes="mg ml-8" />
      </Box>
    ) : row.historyType === 3 ? (
      <Box classes="mg ml-12" maxWidth={"calc(100% - 80px)"}>
        {chat?.parentMessage && (
          <Typography ellipsis type="body2_rg" classes="mg mr-4">
            {"Re: "}
          </Typography>
        )}
        <Tooltip message={chat?.body ?? chat?.attachments[0]?.title}>
          <Typography ellipsis type="body2_rg">
            {chat?.body ?? chat?.attachments[0]?.title}
          </Typography>
        </Tooltip>
      </Box>
    ) : (
      <Box
        classes="mg ml-12"
        maxWidth={
          i18n.language.includes("ko")
            ? "calc(100% - 100px)"
            : "calc(100% - 170px)"
        }
      >
        {row.saveType === 4 && row.logDatas && (
          <Tooltip message={row.logDatas.join(", ")}>
            <Typography ellipsis type="body2_rg">
              {row.logDatas.join(", ")}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };
  const renderHistoryLogItem = (log: LogItem) => {
    const key = `${log.logType}_${log.actionType}`;
    return (
      <Box width={400} classes="clientHistory-item mg ml-8">
        <Box alignItems="center">
          <Typography
            type="body4_rg"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {utilsCommon.renderDateFormat(log.logDate)}
          </Typography>
          <Tooltip
            message={
              log.userName
                ? !log.userName || log.userName === "#NONE_USER_NAME#"
                  ? t("common.unAuthUser")
                  : log.userName
                : log.userId
            }
            maxWidth={"calc(100% - 130px)"}
          >
            <Typography type="body4_rg" classes="mg ml-8" ellipsis>
              {log.userName
                ? !log.userName || log.userName === "#NONE_USER_NAME#"
                  ? t("common.unAuthUser")
                  : log.userName
                : log.userId}
            </Typography>
          </Tooltip>
        </Box>
        <Box classes="mg mt-4" height={20} alignItems="center">
          <Icons
            label={historyIconByName[key]?.icon}
            variant="line"
            size="16"
            isOn
          />
          <Typography type="subtitle2" classes="mg ml-8">
            {historyIconByName[key]?.name}
          </Typography>
          {renderProperDetailLog(log)}
        </Box>
        {log?.historyType === 1 && (
          <Box classes="hover-actions">
            <Tooltip message={t("file.action.download")}>
              <Button
                btnStyle="table-icon"
                onClick={() =>
                  handleDocDownload(
                    log.syncId,
                    getContentDownload,
                    toast,
                    t,
                    "0",
                    log.documnetName
                  )
                }
                leadingIcon={
                  <Icons variant="line" label="download" size={20} />
                }
              />
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  };
  return (
    <Box classes="clientHistory clientWorkgroupBody">
      <Box height={60} alignItems="center">
        <DateDropdown
          filter={filter}
          buttonClick={buttonClick}
          showValue={false}
          setButtonClick={setButtonClick}
          dateFilter={dateFilter}
          changeFilterByKey={changeFilterByKey}
          isClient
        />
        {/* <Button
          label={t("log.goWebCompress")}
          btnStyle="link"
          classes="mg ml-auto mr-20"
          onClick={() => utilsClient.goToComprssedHistory(workgroupId)}
          leadingIcon={<Icons variant="line" label="cardTwo" />}
        /> */}
      </Box>
      {historyListNewCard.current?.length > 0 ? (
        <InfiniteScrollBox
          data={historyListNewCard.current}
          height="429px"
          id="docTable"
          endReached={() =>
            logState.newLogList.lastLogId !== "-1" &&
            changeFilter({ ...filter, logId: logState.newLogList.lastLogId })
          }
        />
      ) : (
        <EmptyFilterPage
          pageType="client_log"
          handleInitFilter={() => {
            changeDateFilter("all");
            changeFilter(INIT_LOG_FILTER);
          }}
        />
      )}
    </Box>
  );
};

export default ClientHistory;
